import type { ComponentType } from 'react';
import type { Quad, Vec2 } from '../geometry';

/** URL / state values of the four scenes, in picker order. */
export const SCENE_IDS = ['kitchen', 'floor', 'wall', 'staircase'] as const;
export type SceneId = (typeof SCENE_IDS)[number];

/** What the stone covers — selects the wording ("on the countertop", "on the floor" …). */
export type SurfaceKind = 'countertop' | 'floor' | 'wall' | 'stairs';

/**
 * One stone surface of a scene. The stone is laid on a flat *plane* whose own rectangle
 * (`size`, in metres) is pinned to `quad` with a perspective transform, so a floor recedes and a
 * wall stays square — see `geometry.ts`.
 */
export interface SceneRegion {
  /** Unique within the scene. */
  id: string;
  /** Where the surface sits in the 1200 × 800 frame (design px; top-left, top-right, bottom-right, bottom-left). */
  quad: Quad;
  /** Real size of the surface in metres: width along top-left → top-right, depth (floor) or height (wall) along top-left → bottom-left. */
  size: readonly [width: number, height: number];
  /** Edge length, in metres, of one repeat of the stone's seamless texture at scale 1. */
  tile: number;
  /**
   * Optional screen-space outline (design px) that trims the surface to its true shape when it is not
   * the whole quad — used by photographs, where a counter is rarely a perfect trapezoid.
   */
  mask?: readonly Vec2[];
  /** Grow the plane by this many px so neighbouring surfaces overlap instead of leaving a hairline. */
  bleed?: number;
  /**
   * Slide the texture by this many metres (across, down) so surfaces cut from the same stone — the
   * treads and risers of a staircase — do not all show the same patch of it.
   */
  offset?: readonly [across: number, down: number];
}

/** Props of every art layer: a unique prefix for the SVG ids it defines (several scenes can share a document). */
export interface ArtProps {
  ids: string;
}

/**
 * Slot for real photography. When a scene's `photo` is set, the drawn art is replaced by the
 * photograph and the stone is laid on the quads and masks measured from it — nothing else changes.
 *
 * To use it: take a 3 : 2 photograph of the room, find each surface's four corners (and, if the shape
 * is not a plain quad, an outline) in photograph pixels scaled to the 1200 × 800 frame, export the
 * objects that stand in front of the surface (a tap, a plant, a chair) as a transparent PNG, and drop
 * the three into `photo`. There is deliberately no upload UI.
 */
export interface ScenePhotoSlot {
  /** The photograph, 3 : 2. */
  src: string;
  alt: string;
  /** Transparent PNG of everything that must stay in front of the stone (optional). */
  foreground?: string;
  /** Per region id: its corners in the photograph and, optionally, an outline in the same frame. */
  regions: Readonly<Record<string, { quad: Quad; mask?: readonly Vec2[] }>>;
}

/**
 * A scene is a typed module: drawn art in four layers plus the regions the stone goes on.
 *
 *   Back   → the room behind the stone
 *   (stone planes + plain matte planes are laid here from `regions`)
 *   Shade  → soft shadows, multiplied over the surfaces
 *   Sheen  → polished highlights, soft-light over the stone only
 *   Front  → everything standing in front of the stone (props, skirting, vignette)
 *
 * Each layer is SVG content in the 1200 × 800 design frame (a `<g>`, never a whole `<svg>`).
 */
export interface SceneDefinition {
  id: SceneId;
  surface: SurfaceKind;
  /** Painter's order: far to near. */
  regions: readonly SceneRegion[];
  Back: ComponentType<ArtProps>;
  Shade: ComponentType<ArtProps>;
  Sheen: ComponentType<ArtProps>;
  Front: ComponentType<ArtProps>;
  /** Whether a tile-seam option makes sense for this surface (floors and walls). */
  seams: boolean;
  /** Real photography replaces the drawn art when set (see `ScenePhotoSlot`). `null` = drawn art. */
  photo: ScenePhotoSlot | null;
}
